/**
 * useDeferredAnimation — schedule an animation dispatch that survives the next
 * state patch.
 *
 * The bug this exists to prevent, which all three event-animation hooks had:
 *
 *   const interaction = InteractionManager.runAfterInteractions(() => setEntries(…))
 *   const handle = setTimeout(prune, DURATION)
 *   return () => { interaction.cancel(); clearTimeout(handle) }   // ← keyed on [events]
 *
 * The effect is keyed on the `events` array, and the callers pass
 * `gameState.events` — a NEW array identity on every state patch. So the cleanup
 * fires on the very next patch and cancels the dispatch before it can run. Any
 * update arriving within one interaction tick of the event silently ate the
 * animation, which is exactly what happens at a phase boundary: a fortify is the
 * last thing a player does, and the next player's placement patch lands
 * immediately behind it. It got much more visible once the bots started making
 * ~8.7 attacks per turn, because then patches stream continuously.
 *
 * A pending dispatch should only be abandoned when the component goes away. That
 * is what this does: handles accumulate in refs and are cancelled on unmount and
 * nowhere else.
 */

import React from 'react'
import { InteractionManager } from 'react-native'

export interface DeferredAnimation {
  /**
   * Run `fn` once the current gesture/interaction has settled. Deferring is
   * deliberate — it keeps the setState re-render off the frame that is still
   * resolving a tap, which is what gave the trail a clean first frame on iOS.
   */
  afterInteractions: (fn: () => void) => void
  /** Run `fn` after `ms`. Used for the "remove finished entries" sweep. */
  later: (fn: () => void, ms: number) => void
}

export function useDeferredAnimation(): DeferredAnimation {
  const interactions = React.useRef<Array<{ cancel: () => void }>>([])
  const timers = React.useRef<Array<ReturnType<typeof setTimeout>>>([])

  React.useEffect(
    () => () => {
      for (const handle of interactions.current) handle.cancel()
      for (const timer of timers.current) clearTimeout(timer)
      interactions.current = []
      timers.current = []
    },
    [],
  )

  return React.useMemo(
    () => ({
      afterInteractions: (fn: () => void) => {
        interactions.current.push(InteractionManager.runAfterInteractions(fn))
      },
      later: (fn: () => void, ms: number) => {
        timers.current.push(setTimeout(fn, ms))
      },
    }),
    [],
  )
}
